import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CompletionCard } from "@/components/CompletionCard";
import { Clock, CheckCircle2, CalendarDays, Newspaper, Building2, ArrowRight, Sparkles, Users, Wallet, FolderOpen, AlertCircle, Gift, Share2 } from "lucide-react";

const fmtEur = (n) => (Number(n) || 0).toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

export default function AssocDashboard() {
  const [asso, setAsso] = useState(null);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [mstats, setMstats] = useState(null);
  const [docCount, setDocCount] = useState(0);
  const [dstats, setDstats] = useState(null);

  useEffect(() => {
    api.get("/associations/me").then((r) => setAsso(r.data.association)).catch(() => {});
    api.get("/associations/me/events").then((r) => setEvents(r.data.items || [])).catch(() => {});
    api.get("/associations/me/news").then((r) => setNews(r.data.items || [])).catch(() => {});
    api.get("/associations/me/members").then((r) => setMstats(r.data.stats || null)).catch(() => {});
    api.get("/associations/me/documents").then((r) => setDocCount((r.data.items || []).length)).catch(() => {});
    api.get("/associations/me/donations").then((r) => setDstats(r.data.stats || null)).catch(() => {});
  }, []);

  const pending = asso && asso.status !== "active";
  const stats = [
    { icon: CalendarDays, label: "Événements", value: events.length, to: "/espace-association/evenements", color: "text-rose-600" },
    { icon: Newspaper, label: "Actualités", value: news.length, to: "/espace-association/actualites", color: "text-violet-600" },
  ];
  const topShared = [...news].filter((n) => (n.shares || 0) > 0).sort((a, b) => (b.shares || 0) - (a.shares || 0)).slice(0, 5);

  return (
    <div className="max-w-4xl" data-testid="assoc-dashboard">
      <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Bonjour {asso?.name || ""} 👋</h1>

      {asso && (
        <div className={`mt-5 rounded-2xl p-5 border ${pending ? "bg-amber-50 border-amber-200" : "bg-emerald-50 border-emerald-200"}`} data-testid="assoc-status-banner">
          <div className="flex items-start gap-3">
            {pending ? <Clock className="text-amber-600 shrink-0" /> : <CheckCircle2 className="text-emerald-600 shrink-0" />}
            <div>
              <div className="font-bold text-slate-900">{pending ? "Votre page est en attente de validation" : "Votre page est en ligne !"}</div>
              <p className="text-sm text-slate-600 mt-1">
                {pending
                  ? "Complétez votre page vitrine dès maintenant. Elle sera visible par le public dès validation par notre équipe (24-48 h). La publication d'événements et d'actualités sera débloquée à ce moment-là."
                  : <>Les visiteurs peuvent consulter votre page. <a href={`/associations/${asso.slug}`} target="_blank" rel="noopener noreferrer" className="text-emerald-700 font-semibold hover:underline">Voir ma page publique →</a></>}
              </p>
              {asso.admin_comment && <p className="text-sm text-slate-500 mt-2 italic">Note de l'équipe : {asso.admin_comment}</p>}
            </div>
          </div>
        </div>
      )}

      {asso?.completion && <CompletionCard pct={asso.completion.pct} missing={asso.completion.missing} editTo="/espace-association/profil" />}

      <div className="mt-6 grid grid-cols-2 gap-4">
        {stats.map((s) => (
          <Link key={s.label} to={s.to} className="card-hover bg-white border border-slate-200 rounded-2xl p-5" data-testid={`assoc-stat-${s.label}`}>
            <s.icon className={s.color} />
            <div className="mt-2 text-3xl font-extrabold">{s.value}</div>
            <div className="text-sm text-slate-500">{s.label}</div>
          </Link>
        ))}
      </div>

      {topShared.length > 0 && (
        <div className="mt-6" data-testid="assoc-top-shared">
          <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Share2 size={18} className="text-violet-600" /> Vos actus les plus partagées</h2>
          <p className="text-sm text-slate-500 mb-3">Plus vous partagez, plus votre association gagne en visibilité locale.</p>
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {topShared.map((n, i) => (
              <Link key={n.id} to="/espace-association/actualites" className="flex items-center gap-3 p-4 hover:bg-slate-50 transition-colors" data-testid={`assoc-top-shared-${n.id}`}>
                <span className="w-6 text-center text-lg font-extrabold text-slate-300">{i + 1}</span>
                <span className="flex-1 min-w-0 truncate font-semibold text-slate-800">{n.title}</span>
                <span className="inline-flex items-center gap-1 text-sm font-bold text-violet-700 shrink-0"><Share2 size={14} /> {n.shares} partage{n.shares > 1 ? "s" : ""}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {mstats && (
        <div className="mt-6" data-testid="assoc-summary">
          <h2 className="font-heading font-bold text-lg mb-3 flex items-center gap-2"><Users size={18} className="text-emerald-700" /> Résumé de l'association</h2>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            <Link to="/espace-association/membres" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-members">
              <Users className="text-emerald-600" size={18} />
              <div className="mt-2 text-2xl font-extrabold">{mstats.total ?? 0}</div>
              <div className="text-xs text-slate-500">Membres</div>
            </Link>
            <Link to="/espace-association/membres" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-uptodate">
              <CheckCircle2 className="text-emerald-600" size={18} />
              <div className="mt-2 text-2xl font-extrabold text-emerald-700">{mstats.up_to_date ?? 0}</div>
              <div className="text-xs text-slate-500">Cotisations à jour</div>
            </Link>
            <Link to="/espace-association/membres" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-pending">
              <Clock className="text-amber-600" size={18} />
              <div className="mt-2 text-2xl font-extrabold text-amber-600">{mstats.dues_pending ?? 0}</div>
              <div className="text-xs text-slate-500">En attente</div>
            </Link>
            <Link to="/espace-association/membres" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-none">
              <AlertCircle className="text-slate-400" size={18} />
              <div className="mt-2 text-2xl font-extrabold text-slate-500">{mstats.dues_none ?? 0}</div>
              <div className="text-xs text-slate-500">Non renseignées</div>
            </Link>
            <Link to="/espace-association/documents" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-docs">
              <FolderOpen className="text-violet-600" size={18} />
              <div className="mt-2 text-2xl font-extrabold">{docCount}</div>
              <div className="text-xs text-slate-500">Documents</div>
            </Link>
            <Link to="/espace-association/dons" className="card-hover bg-white border border-slate-200 rounded-2xl p-4" data-testid="assoc-summary-donations">
              <Gift className="text-rose-600" size={18} />
              <div className="mt-2 text-2xl font-extrabold text-rose-600">{fmtEur(dstats?.this_month)}</div>
              <div className="text-xs text-slate-500">Dons ce mois</div>
            </Link>
          </div>
          {(mstats.dues_pending > 0 || mstats.dues_none > 0) && (
            <Link to="/espace-association/membres" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-amber-700 hover:underline" data-testid="assoc-summary-remind-cta">
              <Wallet size={15} /> {(mstats.dues_pending || 0) + (mstats.dues_none || 0)} cotisation(s) à relancer → gérer les membres
            </Link>
          )}
        </div>
      )}

      <div className="mt-6 grid sm:grid-cols-3 gap-4">
        <Link to="/espace-association/profil" className="card-hover bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid="assoc-quick-profile">
          <Building2 className="text-emerald-600" />
          <div className="mt-2 font-bold">Ma page vitrine</div>
          <p className="text-sm text-slate-500 flex-1">Logo, couverture, présentation, contacts, galerie.</p>
          <span className="mt-2 text-sm text-emerald-700 font-semibold inline-flex items-center gap-1">Compléter <ArrowRight size={14} /></span>
        </Link>
        <Link to="/espace-association/evenements" className="card-hover bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid="assoc-quick-events">
          <CalendarDays className="text-rose-600" />
          <div className="mt-2 font-bold">Mes événements</div>
          <p className="text-sm text-slate-500 flex-1">Publiez vos événements dans l'agenda local.</p>
          <span className="mt-2 text-sm text-emerald-700 font-semibold inline-flex items-center gap-1">Gérer <ArrowRight size={14} /></span>
        </Link>
        <Link to="/espace-association/actualites" className="card-hover bg-white border border-slate-200 rounded-2xl p-5 flex flex-col" data-testid="assoc-quick-news">
          <Sparkles className="text-violet-600" />
          <div className="mt-2 font-bold">Actualités & IA</div>
          <p className="text-sm text-slate-500 flex-1">Rédigez vos actus avec l'assistant IA.</p>
          <span className="mt-2 text-sm text-emerald-700 font-semibold inline-flex items-center gap-1">Publier <ArrowRight size={14} /></span>
        </Link>
      </div>
    </div>
  );
}
